import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { ScreenSizes } from 'constants/sizes';
import { usePublishTradingOfferModal } from '../context/PublishTradingOfferModalContext';

const PublishTradingOfferSuccessContent: FC<{ size: ScreenSizes }> = ({
  size,
}) => {
  const { t } = useTranslate();
  const history = useHistory();
  const { data, actions } = usePublishTradingOfferModal();

  const offer = data.publishedOffer;
  if (!offer) return null;

  const isSellOffer = offer.type === 'SELL';
  const sharesCount = offer.sharesCount?.total || 0;
  const sharePrice = offer.sharePrice || 0;
  const totalAmount = sharePrice * sharesCount;
  const className =
    (offer as { className?: string }).className ||
    (data.tradingOffer as { className?: string })?.className ||
    '-';
  const societyId = typeof offer.society === 'string' ? offer.society : '';

  const reference = `#OF${
    isSellOffer ? 'V' : 'C'
  }-${new Date().getFullYear()}-${
    offer._id?.slice(-6).toUpperCase() || '------'
  }`;
  const sharesLabel = `${formatNumber(sharesCount)} ${className}`;

  const handleViewOffer = () => {
    actions.handleCloseModal();
    history.push(`/detalle-oferta/${societyId}/${offer._id}`);
  };

  const handleBackToMarket = () => {
    actions.handleCloseModal();
    history.push(`/mercado-de-acciones/${societyId}`);
  };

  const titleKey = isSellOffer
    ? 'SellOfferPublishedSuccessTitle'
    : 'BuyOfferPublishedSuccessTitle';
  const descriptionKey = isSellOffer
    ? 'SellOfferPublishedSuccessDescription'
    : 'BuyOfferPublishedSuccessDescription';

  return (
    <Modal.Body className="text-center py-5">
      <div className="mb-4">
        <em className="icon ni ni-check-circle-fill fs-32px text-success" />
      </div>
      <h4 className="mb-2">{t(titleKey)}</h4>
      <p className="text-soft mb-4">{t(descriptionKey)}</p>
      <div className="card card-bordered bg-light mb-4">
        <div className="card-inner py-3">
          <div className="row fs-13px">
            <div className="col-6 my-1 text-left">
              <span className="text-soft">{t('Reference')}:</span>
            </div>
            <div className="col-6 my-1 text-right">
              <strong>{reference}</strong>
            </div>
            <div className="col-6 my-1 text-left">
              <span className="text-soft">{t('Shares')}:</span>
            </div>
            <div className="col-6 my-1 text-right">
              <strong>{sharesLabel}</strong>
            </div>
            <div className="col-6 my-1 text-left">
              <span className="text-soft">{t('Price')}:</span>
            </div>
            <div className="col-6 my-1 text-right">
              <strong>
                {formatCurrencyDecimals(sharePrice)}/{t('Share').toLowerCase()}
              </strong>
            </div>
            <div className="col-6 my-1 text-left">
              <span className="text-soft">{t('TotalAmount')}:</span>
            </div>
            <div className="col-6 my-1 text-right">
              <strong className="text-success">
                {formatCurrencyDecimals(totalAmount)}
              </strong>
            </div>
            <div className="col-6 my-1 text-left">
              <span className="text-soft">{t('Status')}:</span>
            </div>
            <div className="col-6 my-1 text-right">
              <span className="badge badge-dim badge-success">
                {t('Active')}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        className="d-flex justify-content-center flex-wrap mt-4"
        style={{ gap: '8px' }}
      >
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleViewOffer}
        >
          {t('ViewMyOffer')}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleBackToMarket}
        >
          {t('BackToEquityMarket')}
        </button>
      </div>
    </Modal.Body>
  );
};

export default PublishTradingOfferSuccessContent;
